import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
  Image,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { Text, Card, Avatar, useTheme, Divider } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import {
  MapPin,
  Calendar,
  BookOpen,
  Heart,
  Eye,
  Globe,
  Coffee,
} from 'lucide-react-native';
import { Instagram, Youtube, Twitter } from 'lucide-react-native';
import { apiClient } from '@coffeeplexity/api-client';
import { HeaderActions } from '../components/ui/HeaderActions';
import { logger } from '../utils/logger';

interface UserProfile {
  id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  avatar_url?: string;
  country?: string;
  city?: string;
  website_url?: string;
  youtube_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  total_recipes: number;
  total_followers: number;
  total_following: number;
  total_likes_received: number;
  total_views: number;
  created_at: string;
  updated_at: string;
}

export function UserProfileScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const { userId } = route.params as { userId: string };

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    try {
      setError(null);
      const profileData = await apiClient.getUserProfile(userId);
      setProfile(profileData);
    } catch (err: any) {
      logger.error('Error fetching user profile:', err.message);
      setError('Failed to load user profile. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  useEffect(() => {
    if (profile) {
      const displayName =
        profile.display_name ||
        `${profile.first_name || ''} ${profile.last_name || ''}`.trim() ||
        'User';

      navigation.setOptions({
        headerRight: () => (
          <HeaderActions
            shareContent={{
              title: displayName,
              message: `Check out ${displayName}'s profile on Coffeeplexity!${profile.bio ? `\n\n${profile.bio}` : ''}`,
              url: `https://coffeeplexity.com/users/${profile.user_id}`,
            }}
            testID='user-profile-header-actions'
          />
        ),
      });
    }
  }, [profile, navigation]);

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchProfile();
  };

  const openUrl = (url: string) => {
    if (url) {
      Linking.openURL(url).catch(err =>
        logger.error('Failed to open URL:', err)
      );
    }
  };

  if (loading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.loadingContainer}>
          <Coffee size={56} color={theme.colors.onSurfaceVariant} />
          <Text variant='titleLarge' style={styles.loadingTitle}>
            Loading profile...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error || !profile) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.errorContainer}>
          <Coffee size={64} color={theme.colors.onSurfaceVariant} />
          <Text variant='headlineSmall' style={styles.errorTitle}>
            Profile Not Found
          </Text>
          <Text variant='bodyMedium' style={styles.errorText}>
            {error || "The user profile you're looking for doesn't exist."}
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const displayName =
    profile.display_name ||
    `${profile.first_name || ''} ${profile.last_name || ''}`.trim() ||
    'User';

  const location = [profile.city, profile.country].filter(Boolean).join(', ');
  const joinDate = new Date(profile.created_at).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <ScrollView
        style={styles.scrollView}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <View
          style={[styles.header, { backgroundColor: theme.colors.surface }]}
        >
          <View style={styles.avatarContainer}>
            {profile.avatar_url ? (
              <Image
                source={{ uri: profile.avatar_url }}
                style={styles.avatar}
              />
            ) : (
              <Avatar.Icon
                size={100}
                icon={() => <Coffee size={48} color={theme.colors.primary} />}
                style={[styles.avatar, { backgroundColor: '#fdecc4' }]}
              />
            )}
          </View>

          <Text variant='headlineMedium' style={styles.displayName}>
            {displayName}
          </Text>

          {profile.bio && (
            <Text variant='bodyMedium' style={styles.bio}>
              {profile.bio}
            </Text>
          )}

          {location && (
            <View style={styles.locationContainer}>
              <MapPin size={16} color={theme.colors.onSurfaceVariant} />
              <Text variant='bodySmall' style={styles.locationText}>
                {location}
              </Text>
            </View>
          )}

          <View style={styles.joinedContainer}>
            <Calendar size={16} color={theme.colors.onSurfaceVariant} />
            <Text variant='bodySmall' style={styles.joinedText}>
              Joined {joinDate}
            </Text>
          </View>
        </View>

        {/* Stats Section */}
        <Card style={styles.statsCard}>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <BookOpen size={24} color={theme.colors.primary} />
              <Text variant='headlineSmall' style={styles.statValue}>
                {profile.total_recipes}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Recipes
              </Text>
            </View>

            <Divider style={styles.statDivider} />

            <View style={styles.statItem}>
              <Heart size={24} color='#dc2626' />
              <Text variant='headlineSmall' style={styles.statValue}>
                {profile.total_likes_received}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Likes
              </Text>
            </View>

            <Divider style={styles.statDivider} />

            <View style={styles.statItem}>
              <Eye size={24} color={theme.colors.primary} />
              <Text variant='headlineSmall' style={styles.statValue}>
                {profile.total_views}
              </Text>
              <Text variant='bodySmall' style={styles.statLabel}>
                Views
              </Text>
            </View>
          </View>
        </Card>

        {/* Social Links */}
        {(profile.website_url ||
          profile.youtube_url ||
          profile.instagram_url ||
          profile.twitter_url) && (
          <Card style={styles.socialCard}>
            <Text variant='titleMedium' style={styles.sectionTitle}>
              Connect
            </Text>
            <View style={styles.socialLinks}>
              {profile.website_url && (
                <TouchableOpacity
                  style={styles.socialButton}
                  onPress={() => openUrl(profile.website_url!)}
                >
                  <Globe size={20} color={theme.colors.primary} />
                  <Text variant='bodyMedium' style={styles.socialText}>
                    Website
                  </Text>
                </TouchableOpacity>
              )}
              {profile.youtube_url && (
                <TouchableOpacity
                  style={styles.socialButton}
                  onPress={() => openUrl(profile.youtube_url!)}
                >
                  <Youtube size={20} color='#dc2626' />
                  <Text variant='bodyMedium' style={styles.socialText}>
                    YouTube
                  </Text>
                </TouchableOpacity>
              )}
              {profile.instagram_url && (
                <TouchableOpacity
                  style={styles.socialButton}
                  onPress={() => openUrl(profile.instagram_url!)}
                >
                  <Instagram size={20} color='#e1306c' />
                  <Text variant='bodyMedium' style={styles.socialText}>
                    Instagram
                  </Text>
                </TouchableOpacity>
              )}
              {profile.twitter_url && (
                <TouchableOpacity
                  style={styles.socialButton}
                  onPress={() => openUrl(profile.twitter_url!)}
                >
                  <Twitter size={20} color='#1da1f2' />
                  <Text variant='bodyMedium' style={styles.socialText}>
                    Twitter
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </Card>
        )}

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  loadingTitle: {
    marginTop: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorTitle: {
    marginTop: 24,
    marginBottom: 12,
    fontWeight: '600',
    textAlign: 'center',
  },
  errorText: {
    opacity: 0.7,
    textAlign: 'center',
    lineHeight: 22,
  },
  header: {
    padding: 24,
    alignItems: 'center',
  },
  avatarContainer: {
    marginBottom: 16,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  displayName: {
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  bio: {
    textAlign: 'center',
    opacity: 0.8,
    marginBottom: 16,
    lineHeight: 22,
  },
  locationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  locationText: {
    opacity: 0.7,
    marginLeft: 6,
  },
  joinedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  joinedText: {
    opacity: 0.7,
    marginLeft: 6,
  },
  statsCard: {
    margin: 16,
    marginTop: 0,
    padding: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statDivider: {
    width: 1,
    height: 60,
  },
  statValue: {
    fontWeight: '700',
    marginTop: 8,
  },
  statLabel: {
    opacity: 0.7,
    marginTop: 8,
  },
  socialCard: {
    margin: 16,
    marginTop: 0,
    padding: 20,
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  socialLinks: {
    // gap replaced with marginBottom on buttons
  },
  socialButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    marginBottom: 12,
  },
  socialText: {
    fontWeight: '500',
    marginLeft: 12,
  },
  bottomPadding: {
    height: 40,
  },
});
